import { toast } from "react-toastify";

import { STATUS_CODE } from "@/constants/errorCode";
import { MESSAGE } from "@/constants/message";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { localForagePersister, queryClient } from "@/lib/react-query";
import { resetStore, store } from "@/store";
import { setAuth } from "@/store/slice/authSlice";

import { datadogRum } from "@datadog/browser-rum";
import type { AxiosError } from "axios";
import axios from "axios";

// Singleton promise pattern: stores the in-flight refresh promise so that
// concurrent 401 responses all await the same refresh call instead of
// triggering multiple /auth/token requests (which would fail if the
// refresh token is single-use/rotated).
let refreshPromise: Promise<string> | null = null;

export const PATH_WITHOUT_AUTH_HEADER = [
  /\/auth\/token$/,
  /\/auth\/verify-token$/,
  /\/auth\/forgot-password$/,
  /\/auth\/reset-password$/,
  /\/auth\/sso\/providers$/,
  /\/activate-user$/,
  /\/partners\/.+$/ // This matches /partners/ followed by anything
];

const LOGOUT_PATH = /\/auth\/logout$/;

export const baseUrl = import.meta.env.VITE_API_URL || "";

const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json"
  },
  withCredentials: true
});

// This function will only be called when any api / api logout has been retried
export const doLogout = async () => {
  // No matter what happens to the logout API call
  // Reset the store to initial state + purge persisted data
  resetStore();

  toast.dismiss();

  // Clear all react-query cache
  queryClient.clear();
  await localForagePersister.removeClient();

  // Clear datadog user
  datadogRum.clearUser();

  // Call logout API then clear auth state on server side
  showToast({
    title: MESSAGE.SESSION_EXPIRED_TITLE,
    message: MESSAGE.SESSION_EXPIRED_DESCRIPTION,
    type: EToastType.WARNING
  });
  await axiosInstance.post("/v1/auth/logout");
};

const isUnauthorized = (status: number) => status === STATUS_CODE.UNAUTHORIZED;

/**
 * Transport-agnostic access token refresh primitive.
 *
 * Coordinates concurrent callers via `isRefreshing` / `subscribeTokenRefresh`
 * so only one `/v1/auth/token` call fires per refresh cycle — additional
 * callers subscribe and resolve with the same token. On success, updates the
 * Redux auth slice and returns the new access token. On failure, calls
 * `doLogout` before re-throwing.
 *
 * Used by both the axios response interceptor (`doRefreshToken`) and
 * non-axios consumers (e.g. SSE streams in `useEventSource` and
 * `fetchDatasetStream`) that need a fresh token to reopen their own
 * transport.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = axiosInstance
    .post("/v1/auth/token", {
      grant_type: "refresh_token",
      refresh_token: store.getState().auth.refreshToken
    })
    .then((result) => {
      const access_token = result?.data?.access_token as string;
      if (access_token) {
        store.dispatch(setAuth({ accessToken: access_token }));
      }
      return access_token;
    })
    .catch(async (err) => {
      await doLogout();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const doRefreshToken = async (
  originalConfig: AxiosError["config"] & { _retry?: boolean }
) => {
  originalConfig._retry = true;
  const access_token = await refreshAccessToken();
  originalConfig.headers["Authorization"] = `Bearer ${access_token}`;
  return axiosInstance(originalConfig);
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.accessToken;

    // Check if the current URL path should skip auth header
    const shouldSkipAuthHeader = PATH_WITHOUT_AUTH_HEADER.some((pathPattern) =>
      pathPattern.test(config.url ?? "")
    );

    // Only add Authorization header if token exists and URL is not in the excluded list
    if (token && !shouldSkipAuthHeader) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error as Error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalConfig = error.config;
    const currentUrl = originalConfig.url ?? "";
    const shouldSkipRefreshToken =
      LOGOUT_PATH.test(currentUrl) || // if on logout path, skip the refresh token
      PATH_WITHOUT_AUTH_HEADER.some((pathPattern) =>
        pathPattern.test(currentUrl)
      );
    originalConfig._retry = originalConfig._retry ?? false;
    // Check for 401 status and ensure we haven't retried this request already
    if (!shouldSkipRefreshToken && isUnauthorized(error?.response?.status)) {
      if (originalConfig._retry) {
        // Logout user if after success refresh token, the API is still unauthorized
        await doLogout();
      } else {
        return doRefreshToken(originalConfig);
      }
    }
    return Promise.reject(error as Error);
  }
);

export default axiosInstance;
